/**
 * Integrytas Group - UI Layout Delta Override Engine (ULD)
 * Page: ULD.OVERRIDES
 *
 * Edit a workspace's layout delta for a screen as an ordered list of declarative
 * mutations (keyed by component_key), and review collateral-damage telemetry -
 * delta actions whose component_key the latest release no longer ships.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { fetchAllRows } from '../../lib/fetch_all_rows';
import { getForensicActorId } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';

type ActionType = 'hide_element' | 'reorder_index' | 'relabel_component' | 'assign_field_to_tab' | 'set_section' | 'set_col_span';
interface DeltaRow { action: ActionType; component_key: string; value: string }
interface StaleRow { uld_apply_log_id: number; component_key: string; action_type: string; reason: string; occurrence_count: number; last_seen_at: string }

const ACTIONS: ActionType[] = ['hide_element', 'reorder_index', 'relabel_component', 'assign_field_to_tab', 'set_section', 'set_col_span'];
const VALUE_HINT: Record<ActionType, string> = {
  hide_element: '(no value)', reorder_index: 'index #', relabel_component: 'new label',
  assign_field_to_tab: 'tab code', set_section: 'section code', set_col_span: 'span 1-12',
};

function actionToRow(a: Record<string, unknown>): DeltaRow {
  const action = a.action as ActionType;
  const value = action === 'reorder_index' ? String(a.index ?? '')
    : action === 'relabel_component' ? String(a.label ?? '')
    : action === 'assign_field_to_tab' ? String(a.tab ?? '')
    : action === 'set_section' ? String(a.section ?? '')
    : action === 'set_col_span' ? String(a.col_span ?? '')
    : '';
  return { action, component_key: String(a.component_key ?? ''), value };
}
function rowToAction(r: DeltaRow): Record<string, unknown> {
  const base = { action: r.action, component_key: r.component_key.trim() };
  switch (r.action) {
    case 'reorder_index': return { ...base, index: Number(r.value) };
    case 'relabel_component': return { ...base, label: r.value };
    case 'assign_field_to_tab': return { ...base, tab: r.value };
    case 'set_section': return { ...base, section: r.value };
    case 'set_col_span': return { ...base, col_span: Number(r.value) };
    default: return base;
  }
}

const STALE_GRID_COLS: GridColumnDef[] = [
  { key: 'component_key', header: 'Component key', defaultWidth: 220 },
  { key: 'action_type',   header: 'Action',        defaultWidth: 160 },
  { key: 'reason',        header: 'Reason',        defaultWidth: 240 },
  { key: 'occurrence_count', header: 'Hits',       defaultWidth: 80  },
  { key: 'last_seen_at',  header: 'Last seen',     defaultWidth: 180 },
];

export interface UldOverridesPageProps { session: AuthSession }

export function UldOverridesPage({ session }: UldOverridesPageProps) {
  return (
    <PersonalizationProvider pageCode="ULD.OVERRIDES" session={session}>
      <UldOverridesPageInner session={session} />
    </PersonalizationProvider>
  );
}

function UldOverridesPageInner({ session }: UldOverridesPageProps) {
  const { t } = useTranslation();
  const actor = getForensicActorId(session);
  const [pages, setPages] = useState<string[]>([]);
  const [page, setPage] = useState('');
  const [ws, setWs] = useState('');
  const [rows, setRows] = useState<DeltaRow[]>([]);
  const [stale, setStale] = useState<StaleRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [staleFilter, setStaleFilter] = useState('');

  const filteredStale = useMemo(() => {
    const q = staleFilter.trim().toLowerCase();
    if (!q) return stale;
    return stale.filter(w => w.component_key.toLowerCase().includes(q) || w.action_type.toLowerCase().includes(q) || (w.reason ?? '').toLowerCase().includes(q));
  }, [stale, staleFilter]);

  // §17.B13: expand/contract related-children. A stale-key warning is a leaf
  // telemetry row with no loaded child collection, so the control renders inert.
  const detail = useExpandedRows<number>();
  // TODO: wire relations — a stale-key warning loads no children.
  const buildRelatedGroups = (): RelatedChildGroup[] => [];

  useEffect(() => { void (async () => {
    // Complete page-code picker: range-page past the row cap (the catalog can hold
    // thousands of rows). page_id is the stable tiebreaker for pagination.
    const data = await fetchAllRows<{ internal_code: string }>((from, to) =>
      apiDb.from('csm_page_master').select('internal_code').order('internal_code').order('page_id').range(from, to));
    setPages(data.map(r => r.internal_code));
  })(); }, []);

  const load = async () => {
    if (!page) { setRows([]); setStale([]); return; }
    setBusy(true); setError(null); setNote(null);
    try {
      const [d, s] = await Promise.all([
        apiDb.from('uld_layout_delta').select('delta').eq('workspace_code', ws).eq('page_code', page).maybeSingle(),
        apiDb.from('uld_apply_log').select('uld_apply_log_id, component_key, action_type, reason, occurrence_count, last_seen_at').eq('page_code', page).eq('workspace_code', ws).order('last_seen_at', { ascending: false }),
      ]);
      if (d.error) throw d.error;
      const arr = (d.data?.delta ?? []) as Record<string, unknown>[];
      setRows(Array.isArray(arr) ? arr.map(actionToRow) : []);
      setStale((s.data ?? []) as StaleRow[]);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };
  // load is re-created each render; reload only when page or ws changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load();   }, [page, ws]);

  const save = async () => {
    if (!page) return;
    setBusy(true); setError(null); setNote(null);
    try {
      const delta = rows.filter(r => r.component_key.trim()).map(rowToAction);
      const { error: e } = await apiDb.from('uld_layout_delta').upsert(
        { workspace_code: ws, page_code: page, delta, is_active: 'Y', modified_by: actor },
        { onConflict: 'workspace_code,page_code' });
      if (e) throw e;
      setNote(t('uld.saved_count', 'Saved {{count}} delta action(s).', { count: delta.length }));
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };

  const addRow = () => setRows(rs => [...rs, { action: 'hide_element', component_key: '', value: '' }]);
  const patch = (i: number, p: Partial<DeltaRow>) => setRows(rs => rs.map((r, j) => j === i ? { ...r, ...p } : r));
  const move = (i: number, dir: -1 | 1) => setRows(rs => { const n = [...rs]; const j = i + dir; if (j < 0 || j >= n.length) return rs; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const del = (i: number) => setRows(rs => rs.filter((_, j) => j !== i));

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('uld.title', 'Layout Overrides')}</span>
        <select value={page} onChange={e => setPage(e.target.value)} className="h-7 rounded border border-csm-border px-1 text-csm-xs">
          <option value="">{t('uld.select_screen', 'Select screen…')}</option>
          {pages.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <input value={ws} onChange={e => setWs(e.target.value)} placeholder={t('uld.workspace_placeholder', 'workspace (blank = global)')} className="h-7 w-44 rounded border border-csm-border px-1 font-mono text-csm-xs" />
        <button onClick={() => void load()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('uld.reload', 'Reload')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('uld.working', 'Working…')}</span>}
        {note && <span className="text-csm-xs text-green-700">{note}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>

      <div className="flex-1 overflow-auto p-3">
        {error && (
          <ErrorBanner
            message={t('uld.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void load()}
            details_label={t('common.details', 'Details')}
          />
        )}
        {!page && <div className="text-csm-sm text-csm-watermark">{t('uld.empty_no_screen', 'Select a screen to edit its layout delta. Deltas are applied onto the latest core layout at runtime.')}</div>}
        {page && (
          <>
            <section className="mb-5">
              <div className="mb-2 flex items-center gap-2">
                <h3 className="text-csm-sm font-semibold text-csm-ink">{t('uld.delta_actions', 'Delta actions')} <span className="font-mono text-csm-2xs text-csm-watermark">{page}{ws ? ` · ${ws}` : ` · ${t('uld.global', 'global')}`}</span></h3>
                <button onClick={addRow} className="h-6 rounded border border-csm-border bg-white px-2 text-csm-xs hover:bg-csm-surface-alt">{t('uld.add_action', '+ Action')}</button>
                <button onClick={() => void save()} className="h-6 rounded border border-primary bg-primary px-3 text-csm-xs text-white hover:opacity-90">{t('uld.save_delta', 'Save delta')}</button>
              </div>
              {rows.map((r, i) => (
                <div key={i} className="mb-1 flex flex-wrap items-center gap-1">
                  <span className="w-6 text-right text-csm-2xs text-csm-watermark">{i + 1}</span>
                  <select value={r.action} onChange={e => patch(i, { action: e.target.value as ActionType })} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}</select>
                  <input value={r.component_key} onChange={e => patch(i, { component_key: e.target.value })} placeholder={t('uld.component_key_placeholder', 'component_key')} className="h-7 w-44 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                  <input value={r.value} onChange={e => patch(i, { value: e.target.value })} placeholder={t('uld.value_hint.' + r.action, VALUE_HINT[r.action])} disabled={r.action === 'hide_element'} className="h-7 w-40 rounded border border-csm-border px-1 text-csm-xs disabled:bg-csm-surface-alt" />
                  <button onClick={() => move(i, -1)} className="px-1 text-csm-xs text-csm-ink-soft" title={t('uld.up', 'up')}>↑</button>
                  <button onClick={() => move(i, 1)} className="px-1 text-csm-xs text-csm-ink-soft" title={t('uld.down', 'down')}>↓</button>
                  <button onClick={() => del(i)} className="text-csm-xs text-csm-danger">{t('uld.del', 'del')}</button>
                </div>
              ))}
              {rows.length === 0 && (
                <EmptyState variant="no-data" title={t('uld.empty_no_delta', 'No delta — this screen renders its core layout unchanged for this workspace.')} action={<button onClick={addRow} className="rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('uld.add_action', '+ Action')}</button>} />
              )}
            </section>

            <section>
              <div className="mb-2 flex items-center gap-2">
                <h3 className="text-csm-sm font-semibold text-csm-ink">{t('uld.stale_warnings', 'Stale-key Warnings')} <span className="text-csm-2xs text-csm-watermark">{t('uld.stale_warnings_sub', '(delta actions whose component_key the current release no longer ships)')}</span></h3>
                <input type="text" value={staleFilter} onChange={e => setStaleFilter(e.target.value)} placeholder={t('uld.filter', 'Filter…')} className="h-7 w-44 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none" />
              </div>
              <PersonalizedGrid componentKey="grid" columns={STALE_GRID_COLS}>
                {(visibleCols, widths) => (
                  <table className="w-max border-collapse text-csm-sm">
                    <thead><tr className="border-b border-csm-border text-left text-csm-2xs uppercase tracking-wide text-csm-watermark">
                      <th className="w-7 px-2 py-1" title={t('common.related.header', 'Related records')} />
                      {visibleCols.map(col => (
                        <th key={col.key} className="px-2 py-1" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('uld.col.' + col.key, col.header)}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {filteredStale.map(w => (
                        <Fragment key={w.uld_apply_log_id}>
                        <tr className="border-b border-csm-border-soft">
                          <td className="px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                            <RelatedExpandButton open={detail.isOpen(w.uld_apply_log_id)} hasChildren={false} onToggle={() => detail.toggle(w.uld_apply_log_id)} expandLabel={t('common.related.show', 'Show related records')} collapseLabel={t('common.related.hide', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                          </td>
                          {visibleCols.map(col => (
                            <td key={col.key} className="px-2 py-1" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                              {col.key === 'component_key'    && <span className="font-mono text-csm-xs text-csm-danger">{w.component_key}</span>}
                              {col.key === 'action_type'      && <span className="font-mono text-csm-2xs">{w.action_type}</span>}
                              {col.key === 'reason'           && <span className="text-csm-2xs text-csm-ink-soft">{w.reason}</span>}
                              {col.key === 'occurrence_count' && <span className="text-csm-xs">{w.occurrence_count}</span>}
                              {col.key === 'last_seen_at'     && <span className="text-csm-2xs text-csm-watermark">{new Date(w.last_seen_at).toLocaleString()}</span>}
                            </td>
                          ))}
                        </tr>
                        {detail.isOpen(w.uld_apply_log_id) && (
                          <tr><td colSpan={visibleCols.length + 1} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRelatedGroups()} session={session} /></td></tr>
                        )}
                        </Fragment>
                      ))}
                      {filteredStale.length === 0 && (
                        <tr><td colSpan={visibleCols.length + 1} className="px-2 py-3">
                          <EmptyState variant={staleFilter.trim() ? 'no-match' : 'all-clear'} title={t('uld.empty_no_stale', 'No stale-key warnings — every delta action resolves against the current layout.')} />
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </PersonalizedGrid>
              <p className="mt-1 text-csm-2xs text-csm-watermark">{t('uld.stale_note', 'The merger omits these silently so screens still render; remove the matching delta action above to clear a warning.')}</p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
